import { ClipboardCheck, Clock, Home, Route, UserCheck } from "lucide-react";

// Colour accents drawn from the existing Lotus palette
const ACCENTS = {
  sage: "#8EA694",
  terracotta: "#C47C5D",
  green: "#2D5A3F",
  ink: "#1A2E24",
  muted: "#5C7567",
};

const data = {
  da: {
    overview: {
      eyebrow: "Indsatser og forløb",
      title: "Struktureret støtte fra afklaring til fastholdelse",
      intro:
        "Lotusgruppens indsatser er skruet sammen som en samlet produktfamilie, der kan bestilles enkeltvis eller kombineres. Fra en kort afklaring til et helt reintegrationsforløb tilpasser vi indsatsen efter kommunens bestilling og borgerens behov.",
      audienceTitle: "Skabt til bestillere og fagpersoner",
      audienceIntro:
        "Vores forløb henvender sig til de fagpersoner, der visiterer og samarbejder omkring borgeren:",
      audience: [
        "Kommunale sagsbehandlere",
        "Jobcentre",
        "Kriminalforsorgen",
        "Unge- og voksenafdelinger",
        "Socialrådgivere",
        "Beskæftigelses- og myndighedsledere",
        "Samarbejdende virksomheder",
      ],
      guaranteesTitle: "Det kan vi garantere",
      guaranteesIntro:
        "Vi garanterer ikke beskæftigelse eller kriminalitetsfrihed – det ville være useriøst. Til gengæld garanterer vi rammerne, der giver borgeren den bedste chance:",
      guarantees: [
        "En tydeligt beskrevet indsats",
        "Høj tilgængelighed",
        "Løbende dokumentation",
        "Hurtig opfølgning",
        "Fast kontaktperson",
        "Samarbejde med relevante aktører",
      ],
    },
    labels: {
      duration: "Varighed",
      forWhom: "Målgruppe",
      process: "Faser i forløbet",
      deliverables: "Leverancer til bestiller",
      results: "Resultatmål",
      guarantees: "Det garanterer vi",
      price: "Pris",
      viewProduct: "Se forløbet",
      backToOverview: "Alle indsatser",
      family: "Indsatser og forløb",
      flagship: "Flagskibsforløb",
    },
    cta: {
      title: "Har du en konkret borger i tankerne?",
      subtitle:
        "Vi vender hurtigt tilbage og hjælper med at vurdere den rette indsats – uforpligtende.",
      discuss: "Drøft en konkret borger",
      assessment: "Anmod om en faglig vurdering",
      contact: "Kontakt Lotusgruppen",
    },
    products: [
      {
        slug: "lotus-afklaring",
        name: "Lotus Afklaring",
        Icon: ClipboardCheck,
        accent: ACCENTS.sage,
        duration: "14 eller 30 dage",
        tagline: "Kortlægning før den rette indsats vælges",
        summary:
          "Et kort, struktureret afklaringsforløb, der kortlægger borgerens behov, ressourcer, motivation og risici – og munder ud i en konkret anbefaling til næste indsats.",
        intro:
          "Lotus Afklaring giver bestilleren et solidt beslutningsgrundlag, inden der visiteres til et længere forløb. På 14 eller 30 dage møder vi borgeren, afdækker situationen fra flere vinkler og oversætter det til en klar faglig anbefaling.",
        priceNote: "Pris fastsættes efter afklaringens omfang (14 eller 30 dage).",
        forWhom:
          "Sagsbehandlere og myndigheder, der har brug for et fagligt beslutningsgrundlag, før en indsats bestilles.",
        process: [
          { title: "Opstart og samtykke", text: "Fælles opstart med bestiller og borger, aftale om rammer, formål og samtykke." },
          { title: "Kortlægning", text: "Afdækning af bolig, økonomi, helbred, netværk, motivation og risikofaktorer." },
          { title: "Afklaringssamtaler", text: "Strukturerede samtaler med borgeren om mål, ressourcer og barrierer." },
          { title: "Afrapportering", text: "Afklaringsrapport og konkret anbefaling til næste indsats, gennemgået med bestiller." },
        ],
        deliverables: [
          "Afklaringsrapport",
          "Risiko- og ressourceprofil",
          "Konkret anbefaling til næste indsats",
          "Statusmøde med bestiller",
        ],
        results: [
          "Et klart billede af borgerens situation og behov",
          "Fagligt grundlag for visitation",
          "Anbefalet indsatsniveau og -intensitet",
        ],
      },
      {
        slug: "lotus-72",
        name: "Lotus 72",
        subtitle: "løsladelsesberedskab",
        Icon: Clock,
        accent: ACCENTS.terracotta,
        duration: "Fra fire uger før løsladelse til 72 timer efter",
        tagline: "De kritiske timer omkring løsladelsen planlagt og understøttet",
        summary:
          "Et intensivt løsladelsesberedskab, hvor de sårbare første timer og døgn efter løsladelsen planlægges grundigt og understøttes tæt af en fast kontaktperson.",
        intro:
          "Risikoen for tilbagefald er størst i de første dage efter en løsladelse. Lotus 72 sætter ind fra fire uger før og følger borgeren helt til 72 timer efter løsladelsen, så overgangen bliver tryg og sammenhængende.",
        priceNote: "Pris fastsættes efter støttebehov og indsatsintensitet.",
        forWhom:
          "Kriminalforsorgen og sagsbehandlere ved en forestående løsladelse, hvor de første døgn skal sikres.",
        process: [
          { phase: "Fase 1", title: "Planlægning (fra 4 uger før)", text: "Løsladelsesplan, koordinering med myndigheder, sikring af bolig og praktik." },
          { phase: "Fase 2", title: "Modtagelse (løsladelsesdagen)", text: "Personlig modtagelse, transport og praktisk hjælp fra dag ét." },
          { phase: "Fase 3", title: "De første 72 timer", text: "Tæt tilstedeværelse, struktur, akut problemløsning og ro på situationen." },
          { phase: "Fase 4", title: "Overlevering", text: "Bro til et videre forløb (fx Lotus Stabil 90 eller Lotus 180) og statusnotat." },
        ],
        deliverables: [
          "Løsladelsesplan",
          "Praktisk modtagelse og transport",
          "Bolig ved løsladelse",
          "Myndighedskontakt og koordinering",
          "Statusnotat efter 72 timer",
        ],
        results: [
          "Tryg og planlagt overgang ved løsladelse",
          "Færre kritiske brud i de første døgn",
          "En klar bro til det videre forløb",
        ],
      },
      {
        slug: "lotus-stabil-90",
        name: "Lotus Stabil 90",
        Icon: Home,
        accent: ACCENTS.green,
        duration: "Tre måneder",
        tagline: "Stabilisering af hverdagen på tre måneder",
        summary:
          "Et stabiliseringsforløb, der får bolig, økonomi, struktur, behandling og myndighedssamarbejde til at hænge sammen, så borgeren får fast grund under fødderne.",
        intro:
          "Lotus Stabil 90 skaber den ro og struktur, der er en forudsætning for al videre udvikling. Over tre måneder arbejder vi systematisk med de grundlæggende dele af borgerens hverdag sammen med en fast kontaktperson.",
        priceNote: "Forløbet tilpasses kommunens bestilling og borgerens behov.",
        forWhom:
          "Sagsbehandlere og myndigheder, hvor borgeren har brug for at få styr på det basale, før udvikling og beskæftigelse kan begynde.",
        process: [
          { title: "Kortlægning og plan", text: "Fælles mål og en konkret stabiliseringsplan." },
          { title: "Bolig og økonomi", text: "Sikring af bolig, budget, betaling af regninger og kontakt til relevante instanser." },
          { title: "Struktur og behandling", text: "Døgnrytme, faste aftaler og kontakt til fx læge eller misbrugsbehandling." },
          { title: "Status og overlevering", text: "Statusnotat og anbefaling om eventuelt videre forløb." },
        ],
        deliverables: [
          "Stabiliseringsplan",
          "Fast kontaktperson",
          "Løbende statusnotater",
          "Koordinering med myndigheder og behandling",
        ],
        results: [
          "Stabil boligsituation",
          "Overblik og styr på økonomi",
          "Fast døgnrytme og struktur i hverdagen",
          "Kontakt til relevant behandling",
        ],
      },
      {
        slug: "lotus-180",
        name: "Lotus 180",
        subtitle: "fra løsladelse til arbejde",
        Icon: Route,
        accent: ACCENTS.ink,
        flagship: true,
        duration: "Seks måneder – med mulighed for forlængelse",
        tagline: "Fra løsladelse til arbejde – vores flagskibsforløb",
        summary:
          "Et helhedsorienteret forløb over seks måneder, der følger borgeren fra tiden før løsladelsen hele vejen til fastholdelse i arbejde eller uddannelse. Opbygget omkring fem faser.",
        intro:
          "Lotus 180 er Lotusgruppens flagskibsforløb. Det samler afklaring, stabilisering, udvikling og beskæftigelse i ét sammenhængende forløb med én fast kontaktperson – og kan forlænges, hvis borgerens situation kræver det.",
        priceNote: "Pris fastsættes efter støttebehov og indsatsintensitet.",
        forWhom:
          "Kommuner, jobcentre og Kriminalforsorgen, der ønsker et samlet, dokumenteret forløb med sigte på beskæftigelse eller uddannelse.",
        process: [
          { phase: "Fase 1", title: "Før løsladelsen", text: "Planlægning, relationsopbygning og forberedelse af bolig, økonomi og myndighedskontakt." },
          { phase: "Fase 2", title: "Opstart", text: "Tryg modtagelse ved løsladelse, etablering af struktur og de første aftaler." },
          { phase: "Fase 3", title: "Stabilisering", text: "Bolig, økonomi, behandling og døgnrytme bringes i orden." },
          { phase: "Fase 4", title: "Udvikling", text: "Kompetencer, praktik, uddannelse og virksomhedssamarbejde bygges op." },
          { phase: "Fase 5", title: "Fastholdelse", text: "Tæt opfølgning i job/uddannelse og hurtig indsats ved risiko for frafald." },
        ],
        deliverables: [
          "Samlet forløbsplan over alle fem faser",
          "Fast kontaktperson gennem hele forløbet",
          "Bolig- og økonomistøtte",
          "Beskæftigelses- og uddannelsesindsats",
          "Virksomhedssamarbejde og praktikaftaler",
          "Løbende dokumentation og statusmøder",
        ],
        results: [
          "En reel vej mod ordinær beskæftigelse eller uddannelse",
          "Stabil hverdag og struktur",
          "Styrket netværk og selvstændighed",
          "Dokumenteret progression gennem alle faser",
        ],
      },
      {
        slug: "lotus-fastholdelse",
        name: "Lotus Fastholdelse",
        Icon: UserCheck,
        accent: ACCENTS.muted,
        duration: "Tre, seks eller tolv måneder",
        tagline: "Opfølgning der holder fast, når hverdagen er begyndt",
        summary:
          "Et opfølgende forløb efter opstart i praktik, uddannelse eller arbejde – med hurtig indsats ved fravær, konflikt eller risiko for frafald.",
        intro:
          "Mange forløb lykkes i opstarten, men falder fra hen ad vejen. Lotus Fastholdelse er den forsikring, der sikrer, at borgeren bliver i praktikken, uddannelsen eller arbejdet – med en fast kontaktperson og et hurtigt beredskab.",
        priceNote: "Vælg tre, seks eller tolv måneder – pris fastsættes efter behov.",
        forWhom:
          "Jobcentre, virksomheder og sagsbehandlere, der vil sikre, at en opstart i praktik, uddannelse eller job holder.",
        process: [
          { title: "Overlevering og mål", text: "Klare mål for fastholdelsen og aftaler med borger, arbejdsplads og bestiller." },
          { title: "Løbende opfølgning", text: "Faste kontakter og tilstedeværelse omkring praktik, uddannelse eller job." },
          { title: "Hurtig indsats", text: "Straks-indsats ved fravær, konflikt eller tegn på risiko for frafald." },
          { title: "Afslutning og videre plan", text: "Statusnotat og aftale om eventuel fortsat støtte." },
        ],
        deliverables: [
          "Fastholdelsesplan",
          "Fast kontaktperson",
          "Hurtig-indsats-beredskab",
          "Statusnotater til bestiller",
        ],
        results: [
          "Fastholdelse i praktik, uddannelse eller arbejde",
          "Færre frafald",
          "Hurtig håndtering af kriser og konflikter",
        ],
      },
    ],
  },

  en: {
    overview: {
      eyebrow: "Programmes & pathways",
      title: "Structured support from assessment to retention",
      intro:
        "Lotusgruppen's programmes are designed as one product family that can be ordered individually or combined. From a short assessment to a full reintegration pathway, we tailor the effort to the municipality's order and the citizen's needs.",
      audienceTitle: "Built for commissioners and professionals",
      audienceIntro:
        "Our pathways are aimed at the professionals who refer and cooperate around the citizen:",
      audience: [
        "Municipal caseworkers",
        "Job centres",
        "The Prison and Probation Service",
        "Youth and adult departments",
        "Social workers",
        "Employment and authority managers",
        "Partner companies",
      ],
      guaranteesTitle: "What we can guarantee",
      guaranteesIntro:
        "We do not guarantee employment or a crime-free life – that would be irresponsible. What we do guarantee is the framework that gives the citizen the best possible chance:",
      guarantees: [
        "A clearly described effort",
        "High availability",
        "Ongoing documentation",
        "Fast follow-up",
        "A fixed contact person",
        "Cooperation with relevant partners",
      ],
    },
    labels: {
      duration: "Duration",
      forWhom: "Target group",
      process: "Phases of the pathway",
      deliverables: "Deliverables to the commissioner",
      results: "Result goals",
      guarantees: "What we guarantee",
      price: "Price",
      viewProduct: "View pathway",
      backToOverview: "All programmes",
      family: "Part of Programmes & pathways",
      flagship: "Flagship pathway",
    },
    cta: {
      title: "Do you have a specific citizen in mind?",
      subtitle:
        "We respond quickly and help assess the right programme – with no obligation.",
      discuss: "Discuss a specific citizen",
      assessment: "Request a professional assessment",
      contact: "Contact Lotusgruppen",
    },
    products: [
      {
        slug: "lotus-afklaring",
        name: "Lotus Assessment",
        Icon: ClipboardCheck,
        accent: ACCENTS.sage,
        duration: "14 or 30 days",
        tagline: "Mapping before the right programme is chosen",
        summary:
          "A short, structured assessment that maps the citizen's needs, resources, motivation and risks – resulting in a concrete recommendation for the next step.",
        intro:
          "Lotus Assessment gives the commissioner a solid basis for decision before referring to a longer programme. In 14 or 30 days we meet the citizen, map the situation from several angles and translate it into a clear professional recommendation.",
        priceNote: "Price is set according to the scope of the assessment (14 or 30 days).",
        forWhom:
          "Caseworkers and authorities who need a professional basis for decision before ordering a programme.",
        process: [
          { title: "Start-up and consent", text: "Joint start-up with commissioner and citizen; agreement on framework, purpose and consent." },
          { title: "Mapping", text: "Assessment of housing, finances, health, network, motivation and risk factors." },
          { title: "Assessment conversations", text: "Structured conversations with the citizen about goals, resources and barriers." },
          { title: "Reporting", text: "Assessment report and concrete recommendation, reviewed with the commissioner." },
        ],
        deliverables: [
          "Assessment report",
          "Risk and resource profile",
          "Concrete recommendation for the next programme",
          "Status meeting with the commissioner",
        ],
        results: [
          "A clear picture of the citizen's situation and needs",
          "A professional basis for referral",
          "Recommended level and intensity of support",
        ],
      },
      {
        slug: "lotus-72",
        name: "Lotus 72",
        subtitle: "release readiness",
        Icon: Clock,
        accent: ACCENTS.terracotta,
        duration: "From four weeks before release to 72 hours after",
        tagline: "The critical hours around release, planned and supported",
        summary:
          "An intensive release readiness programme where the vulnerable first hours and days after release are carefully planned and closely supported by a fixed contact person.",
        intro:
          "The risk of relapse is greatest in the first days after release. Lotus 72 starts four weeks before and follows the citizen all the way to 72 hours after release, making the transition safe and coherent.",
        priceNote: "Price is set according to support needs and intensity.",
        forWhom:
          "The Prison and Probation Service and caseworkers facing an upcoming release where the first days must be secured.",
        process: [
          { phase: "Phase 1", title: "Planning (from 4 weeks before)", text: "Release plan, coordination with authorities, securing housing and activities." },
          { phase: "Phase 2", title: "Reception (release day)", text: "Personal reception, transport and practical help from day one." },
          { phase: "Phase 3", title: "The first 72 hours", text: "Close presence, structure, acute problem-solving and calm." },
          { phase: "Phase 4", title: "Handover", text: "A bridge to a further programme (e.g. Lotus Stabil 90 or Lotus 180) and a status note." },
        ],
        deliverables: [
          "Release plan",
          "Practical reception and transport",
          "Housing at release",
          "Authority contact and coordination",
          "Status note after 72 hours",
        ],
        results: [
          "A safe and planned transition at release",
          "Fewer critical breakdowns in the first days",
          "A clear bridge to the further programme",
        ],
      },
      {
        slug: "lotus-stabil-90",
        name: "Lotus Stabil 90",
        Icon: Home,
        accent: ACCENTS.green,
        duration: "Three months",
        tagline: "Stabilising everyday life in three months",
        summary:
          "A stabilisation programme that brings housing, finances, structure, treatment and authority cooperation together, giving the citizen firm ground to stand on.",
        intro:
          "Lotus Stabil 90 creates the calm and structure that is a prerequisite for all further development. Over three months we work systematically on the basics of the citizen's everyday life together with a fixed contact person.",
        priceNote: "The programme is tailored to the municipality's order and the citizen's needs.",
        forWhom:
          "Caseworkers and authorities where the citizen needs to sort out the basics before development and employment can begin.",
        process: [
          { title: "Mapping and plan", text: "Shared goals and a concrete stabilisation plan." },
          { title: "Housing and finances", text: "Securing housing, budget, paying bills and contact with relevant bodies." },
          { title: "Structure and treatment", text: "Daily rhythm, fixed appointments and contact with e.g. GP or addiction treatment." },
          { title: "Status and handover", text: "Status note and recommendation for a possible further programme." },
        ],
        deliverables: [
          "Stabilisation plan",
          "Fixed contact person",
          "Ongoing status notes",
          "Coordination with authorities and treatment",
        ],
        results: [
          "A stable housing situation",
          "Overview and control of finances",
          "A fixed daily rhythm and structure",
          "Contact with relevant treatment",
        ],
      },
      {
        slug: "lotus-180",
        name: "Lotus 180",
        subtitle: "from release to work",
        Icon: Route,
        accent: ACCENTS.ink,
        flagship: true,
        duration: "Six months – with the option to extend",
        tagline: "From release to work – our flagship pathway",
        summary:
          "A holistic six-month pathway that follows the citizen from before release all the way to retention in work or education. Built around five phases.",
        intro:
          "Lotus 180 is Lotusgruppen's flagship pathway. It brings assessment, stabilisation, development and employment together into one coherent pathway with a single fixed contact person – and can be extended if the citizen's situation requires it.",
        priceNote: "Price is set according to support needs and intensity.",
        forWhom:
          "Municipalities, job centres and the Prison and Probation Service wanting a complete, documented pathway aimed at employment or education.",
        process: [
          { phase: "Phase 1", title: "Before release", text: "Planning, relationship-building and preparation of housing, finances and authority contact." },
          { phase: "Phase 2", title: "Start-up", text: "Safe reception at release, establishing structure and the first agreements." },
          { phase: "Phase 3", title: "Stabilisation", text: "Housing, finances, treatment and daily rhythm are put in order." },
          { phase: "Phase 4", title: "Development", text: "Skills, work placements, education and company cooperation are built up." },
          { phase: "Phase 5", title: "Retention", text: "Close follow-up in job/education and fast action at risk of drop-out." },
        ],
        deliverables: [
          "A complete plan across all five phases",
          "A fixed contact person throughout",
          "Housing and financial support",
          "Employment and education effort",
          "Company cooperation and placement agreements",
          "Ongoing documentation and status meetings",
        ],
        results: [
          "A real path towards ordinary employment or education",
          "A stable everyday life and structure",
          "A strengthened network and independence",
          "Documented progression through all phases",
        ],
      },
      {
        slug: "lotus-fastholdelse",
        name: "Lotus Retention",
        Icon: UserCheck,
        accent: ACCENTS.muted,
        duration: "Three, six or twelve months",
        tagline: "Follow-up that holds firm once everyday life has begun",
        summary:
          "A follow-up programme after starting a work placement, education or job – with fast action in case of absence, conflict or risk of drop-out.",
        intro:
          "Many pathways succeed at the start but fall apart along the way. Lotus Retention is the insurance that keeps the citizen in the placement, education or job – with a fixed contact person and a rapid response.",
        priceNote: "Choose three, six or twelve months – price is set according to needs.",
        forWhom:
          "Job centres, companies and caseworkers who want to ensure that a start in a placement, education or job holds.",
        process: [
          { title: "Handover and goals", text: "Clear retention goals and agreements with citizen, workplace and commissioner." },
          { title: "Ongoing follow-up", text: "Fixed contacts and presence around the placement, education or job." },
          { title: "Rapid response", text: "Immediate action at absence, conflict or signs of drop-out risk." },
          { title: "Conclusion and further plan", text: "Status note and agreement on any continued support." },
        ],
        deliverables: [
          "Retention plan",
          "Fixed contact person",
          "Rapid-response readiness",
          "Status notes to the commissioner",
        ],
        results: [
          "Retention in placement, education or work",
          "Fewer drop-outs",
          "Fast handling of crises and conflicts",
        ],
      },
    ],
  },
};

export const getIndsatser = (lang) => data[lang] || data.da;
export const getProduct = (lang, slug) =>
  (data[lang] || data.da).products.find((p) => p.slug === slug);

// ---- Comparison metadata (single source of truth) ----
const compareMeta = {
  da: {
    title: "Sammenlign de fem forløb",
    intro:
      "Få hurtigt overblik over, hvilket forløb der passer til borgerens aktuelle situation og støttebehov.",
    columns: {
      product: "Forløb",
      situation: "Typisk situation",
      duration: "Varighed",
      intensity: "Kontaktintensitet",
      purpose: "Primært formål",
      delivery: "Leverance",
    },
    help: {
      title: "Er du i tvivl om det rette forløb?",
      text:
        "Lotusgruppen kan foretage en indledende faglig vurdering og anbefale den indsats, der bedst matcher borgerens aktuelle situation og støttebehov.",
      cta: "Få hjælp til at vælge forløb",
    },
  },
  en: {
    title: "Compare the five pathways",
    intro:
      "Quickly get an overview of which pathway fits the citizen's current situation and support needs.",
    columns: {
      product: "Pathway",
      situation: "Typical situation",
      duration: "Duration",
      intensity: "Contact intensity",
      purpose: "Primary purpose",
      delivery: "Deliverable",
    },
    help: {
      title: "Unsure which pathway is right?",
      text:
        "Lotusgruppen can carry out an initial professional assessment and recommend the effort that best matches the citizen's current situation and support needs.",
      cta: "Get help choosing a pathway",
    },
  },
};

// Per-slug comparison values (duration/name/flagship come from products)
const compareValues = {
  da: {
    "lotus-afklaring": {
      situation: "Behov og støtteintensitet er endnu uklare",
      intensity: "2–4 samtaler ugentligt",
      purpose: "Afklaring og anbefaling",
      delivery: "Afklaringsrapport og indsatsplan",
    },
    "lotus-72": {
      situation: "Borgeren står foran løsladelse",
      intensity: "Høj og intensiv",
      purpose: "Sikker overgang fra institution til samfund",
      delivery: "Konkret løsladelses- og overgangsplan",
    },
    "lotus-stabil-90": {
      situation: "Ustabilitet forhindrer behandling, uddannelse eller beskæftigelse",
      intensity: "2–5 kontakter ugentligt efter behov",
      purpose: "Grundlæggende stabilisering",
      delivery: "Månedlig status og tværfaglig opfølgning",
    },
    "lotus-180": {
      situation: "Borgeren har behov for et samlet resocialiserings- og beskæftigelsesforløb",
      intensity: "Individuelt tilpasset",
      purpose: "Fra løsladelse til stabilisering, udvikling og arbejde",
      delivery: "Progressionsdokumentation gennem fem faser",
    },
    "lotus-fastholdelse": {
      situation: "Borgeren er startet i praktik, uddannelse eller arbejde",
      intensity: "Som udgangspunkt ugentlig kontakt",
      purpose: "Forebygge fravær, konflikt og frafald",
      delivery: "Månedlig progression og opfølgning",
    },
  },
  en: {
    "lotus-afklaring": {
      situation: "Needs and support intensity are still unclear",
      intensity: "2–4 conversations weekly",
      purpose: "Assessment and recommendation",
      delivery: "Assessment report and action plan",
    },
    "lotus-72": {
      situation: "The citizen is facing release",
      intensity: "High and intensive",
      purpose: "A safe transition from institution to society",
      delivery: "A concrete release and transition plan",
    },
    "lotus-stabil-90": {
      situation: "Instability prevents treatment, education or employment",
      intensity: "2–5 contacts weekly as needed",
      purpose: "Basic stabilisation",
      delivery: "Monthly status and interdisciplinary follow-up",
    },
    "lotus-180": {
      situation: "The citizen needs a complete resocialisation and employment pathway",
      intensity: "Individually adapted",
      purpose: "From release to stabilisation, development and work",
      delivery: "Progression documentation across five phases",
    },
    "lotus-fastholdelse": {
      situation: "The citizen has started a placement, education or job",
      intensity: "Weekly contact as a starting point",
      purpose: "Prevent absence, conflict and drop-out",
      delivery: "Monthly progression and follow-up",
    },
  },
};

export const getCompare = (lang) => {
  const L = data[lang] ? lang : "da";
  const rows = data[L].products.map((p) => ({
    slug: p.slug,
    name: p.name,
    subtitle: p.subtitle,
    flagship: !!p.flagship,
    accent: p.accent,
    duration: p.duration,
    ...compareValues[L][p.slug],
  }));
  return { ...compareMeta[L], rows };
};

// ---- PDF product-sheet labels ----
export const pdfMeta = {
  da: {
    button: "Hent produktark som PDF",
    org: "Lotusgruppen ApS",
    docKicker: "Produktark – Indsatser og forløb",
    purpose: "Formål",
    situation: "Typisk situation",
    forWhom: "Målgruppe",
    duration: "Varighed",
    elements: "Centrale elementer i indsatsen",
    deliverables: "Leverancer til bestiller",
    results: "Mulige resultatmål",
    guarantees: "Det garanterer Lotusgruppen",
    price: "Pris",
    priceNote:
      "Pris fastsættes efter støttebehov, indsatsintensitet og den konkrete bestilling.",
    contact: "Kontakt",
    phoneLabel: "Telefon",
    emailLabel: "Email",
    addressLabel: "Adresse",
    footer: "Lotusgruppen ApS · Peter Sabroes Gade 7, 2th · 2450 København SV",
  },
  en: {
    button: "Download product sheet (PDF)",
    org: "Lotusgruppen ApS",
    docKicker: "Product sheet – Programmes & pathways",
    purpose: "Purpose",
    situation: "Typical situation",
    forWhom: "Target group",
    duration: "Duration",
    elements: "Core elements of the effort",
    deliverables: "Deliverables to the commissioner",
    results: "Possible result goals",
    guarantees: "What Lotusgruppen guarantees",
    price: "Price",
    priceNote:
      "Price is set according to support needs, intensity and the specific order.",
    contact: "Contact",
    phoneLabel: "Phone",
    emailLabel: "Email",
    addressLabel: "Address",
    footer: "Lotusgruppen ApS · Peter Sabroes Gade 7, 2th · 2450 Copenhagen SV",
  },
};
